import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, MapPin, Radar, RefreshCw, BarChart3, ChevronDown, Building2, Trophy, Store, Landmark, Clock, Ruler, History, Crosshair, DollarSign, Repeat, Satellite, ShieldCheck, Check, Smartphone, Users, Database, Globe, Eye, MonitorPlay, Tv, Video, Image } from "lucide-react";
import SiteShell from "../components/site/SiteShell";
import useReveal from "../lib/useReveal";
import useSEO from "../lib/useSEO";

const steps = [
  {
    icon: <MapPin size={28} strokeWidth={1.2} />,
    title: "Fence",
    desc: "We draw invisible perimeters around real-world locations — competitor stores, stadiums, event venues, entire neighborhoods — as tight as a single building or parking lot.",
  },
  {
    icon: <Radar size={28} strokeWidth={1.2} />,
    title: "Capture",
    desc: "When a mobile device enters the fence, it's captured into a privacy-safe audience — layered with demographics and psychographics. Dwell-time filters separate real visitors from drive-by traffic.",
  },
  {
    icon: <RefreshCw size={28} strokeWidth={1.2} />,
    title: "Retarget",
    desc: "Your ads follow that audience across 600,000+ premium apps, websites and streaming platforms — display, video and OTT/CTV — for days, weeks or months after their visit.",
  },
  {
    icon: <BarChart3 size={28} strokeWidth={1.2} />,
    title: "Measure",
    desc: "Conversion zones around your own location track who physically walked in after seeing your ads. Impressions, clicks and real foot traffic — reported live.",
  },
];

const dataSources = [
  { icon: <Smartphone size={20} strokeWidth={1.3} />, source: "Apple & Google", def: "Location data, device signals and app behavior." },
  { icon: <Users size={20} strokeWidth={1.3} />, source: "Social Media", def: "Interests, engagement and social behavior." },
  { icon: <Landmark size={20} strokeWidth={1.3} />, source: "U.S. Census Bureau", def: "Income, household and demographic segmentation." },
  { icon: <Database size={20} strokeWidth={1.3} />, source: "Data Providers", def: "Credit tiers, purchase behavior and lifestyle signals." },
  { icon: <Globe size={20} strokeWidth={1.3} />, source: "Location Intelligence", def: "Real-world movement, visits and patterns." },
];

const privacyPoints = [
  "No personally identifiable information (PII) is accessed or stored",
  "All data is anonymized and aggregated",
  "Device IDs are encrypted and privacy-compliant",
  "Fully aligned with GDPR, CCPA and platform policies",
  "No access to names, phone numbers or personal accounts",
  "Targeting is based on behavioral patterns, not identity",
];

const psychology = [
  {
    id: "eye-contact",
    title: "Eye Contact",
    stat: "+20–40%",
    statLabel: "engagement",
    desc: "Ads where subjects look directly into the camera can increase engagement by 20–40%. The eyes capture — and control — attention.",
    imgA: "/g101/ad-eye-a.jpg",
    imgB: "/g101/ad-eye-b.jpg",
    winner: "A",
  },
  {
    id: "face",
    title: "Human Face & Expression",
    stat: "600%",
    statLabel: "faster than text",
    desc: "The brain processes faces 600% faster than text — and emotional expressions outperform neutral faces by 20–30%. Emotion creates visceral impact and memorability.",
    imgA: "/g101/ad-face-a.jpg",
    imgB: "/g101/ad-face-b.jpg",
    winner: "B",
  },
  {
    id: "contrast",
    title: "Contrast & Brightness",
    stat: "+15–25%",
    statLabel: "click-through rate",
    desc: "High-contrast, brighter creatives can improve CTR by 15–25%. Simplicity removes friction and accelerates action.",
    imgA: "/g101/ad-contrast-a.jpg",
    imgB: "/g101/ad-contrast-b.jpg",
    winner: "A",
  },
  {
    id: "cta",
    title: "CTA Placement",
    stat: "+15–30%",
    statLabel: "engagement",
    desc: "Moving the call-to-action to the top of the creative can increase engagement by 15–30%. Relevance triggers immediate response.",
    imgA: "/g101/ad-cta-a.jpg",
    imgB: "/g101/ad-cta-b.jpg",
    winner: "A",
  },
];

const placements = [
  {
    icon: <Tv size={28} strokeWidth={1.2} />,
    kicker: "OTT / CTV",
    title: "Streaming TV",
    desc: "Your commercial plays full-screen on the biggest screen in the house — served to the exact households that crossed your fence.",
    points: [
      "Runs on streaming platforms like Hulu, Roku, Fire TV and live-sports streams",
      "Full-screen, sound-on, non-skippable placements",
      "Household-level matching from fence data",
      "Premium brand credibility of television — without TV budgets",
    ],
    bestFor: "Big-ticket purchases, brand authority, household decisions",
  },
  {
    icon: <Video size={28} strokeWidth={1.2} />,
    kicker: "Video",
    title: "In-App & Web Video",
    desc: "15–30 second video ads that play before and during the content your audience already watches on their phones and laptops.",
    points: [
      "Pre-roll and mid-roll across premium apps and sites",
      "60%+ completion rates in our demo reporting",
      "Motion and sound drive emotional recall",
      "Click-through straight to your site or offer",
    ],
    bestFor: "Storytelling, promotions, showing the product in action",
  },
  {
    icon: <Image size={28} strokeWidth={1.2} />,
    kicker: "Static Display",
    title: "Display Banners",
    desc: "High-frequency banner ads across 600,000+ apps and websites — the workhorse that keeps your brand in view for weeks after the visit.",
    points: [
      "Every standard size, designed in your branding",
      "Highest impression volume per dollar",
      "Frequency builds recognition and trust",
      "Retargets for up to 30+ days after the fence visit",
    ],
    bestFor: "Foot traffic, offers, staying top-of-mind at scale",
  },
];

const terms = [
  { icon: <MapPin size={20} strokeWidth={1.3} />, term: "Geofence", def: "The virtual perimeter drawn around a physical location where devices are captured." },
  { icon: <Clock size={20} strokeWidth={1.3} />, term: "Dwell Time", def: "How long a device stays inside the fence — filters real visitors from pass-through traffic." },
  { icon: <History size={20} strokeWidth={1.3} />, term: "CTR", def: "Click-through rate — the percentage of people who click your ad after seeing it. A key measure of how well your creative connects." },
  { icon: <Crosshair size={20} strokeWidth={1.3} />, term: "Conversion Zone", def: "A fence around your own location that measures who visited after seeing your ads." },
  { icon: <DollarSign size={20} strokeWidth={1.3} />, term: "CPM", def: "Cost per 1,000 impressions — how programmatic media is priced and bought." },
  { icon: <Repeat size={20} strokeWidth={1.3} />, term: "Frequency Cap", def: "The maximum times one person sees your ad — enough to convert, never enough to annoy." },
];

const useCases = [
  { icon: <Building2 size={24} strokeWidth={1.2} />, title: "Competitor Conquesting", desc: "Fence competitor locations and put your offer in front of their customers." },
  { icon: <Trophy size={24} strokeWidth={1.2} />, title: "Events & Stadiums", desc: "Capture thousands of engaged attendees at games, concerts and conferences." },
  { icon: <Store size={24} strokeWidth={1.2} />, title: "Retail & Foot Traffic", desc: "Pull shoppers from malls and shopping districts through your doors — and prove it." },
  { icon: <Landmark size={24} strokeWidth={1.2} />, title: "Neighborhood Targeting", desc: "Reach specific communities layered with household income and demographics." },
];

const faqs = [
  {
    q: "Is geofencing legal and privacy-compliant?",
    a: "Yes. Audiences are built from anonymized, opt-in mobile location data — never names, phone numbers or personal identities. Our practices fully comply with GDPR, CCPA and U.S. privacy regulations, and sensitive categories (healthcare, legal, and similar) are screened and handled under special compliance rules on every campaign. We target behavior — not individuals.",
  },
  {
    q: "How precise can a geofence be?",
    a: "Down to a single building, storefront or parking lot. We draw custom polygons — not just circles — so a fence can trace the exact outline of a competitor's property without bleeding into neighbors.",
  },
  {
    q: "How long can we retarget someone after they leave a fence?",
    a: "Standard retargeting windows run 30 days, and extended windows are available. We can also build audiences retroactively from up to 60 days of historical visits — capturing people who visited a location before your campaign even started.",
  },
  {
    q: "What ad formats can we run?",
    a: "Static display banners, video, and OTT/CTV (streaming TV) — each with its own CPM and impression volume. Most campaigns blend two or more formats for frequency across devices.",
  },
  {
    q: "What if we don't have ad creative?",
    a: "We produce it. Our Ad Creation add-on ($300) covers professionally designed, on-brand creative in every size and format your campaign needs — engineered around the psychology principles above.",
  },
  {
    q: "How fast can a campaign launch?",
    a: "Typically within a few business days of brief approval. Build your campaign in about five minutes with our Campaign Builder, and our team takes it from there.",
  },
];

export default function Geofencing101Page() {
  useReveal();
  useSEO({
    title: "Geofencing 101 — How Location-Based Advertising Works",
    description: "How geofencing works: military-grade location intelligence, fence real-world locations, capture visitors, retarget across 600,000+ apps, and measure real foot traffic.",
    path: "/geofencing-101",
  });
  return (
    <SiteShell>
      <section className="pt-32 pb-20 border-b border-white/5" data-testid="g101-hero">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div>
            <div className="kicker mb-6">Geofencing 101</div>
            <h1 className="h-display text-white text-[48px] sm:text-[72px] lg:text-[88px] leading-[0.9]">
              LOCATION IS<br /><span className="text-[#d4af37]">INTENT.</span>
            </h1>
            <p className="mt-8 max-w-[600px] text-white/70 text-[16px] leading-relaxed">
              Where someone physically goes says more about what they'll buy than
              anything they type into a search bar. Geofencing turns real-world
              movement into your most precise advertising audience. Here's how it works.
            </p>
          </div>
          <div className="border border-[#d4af37]/15 overflow-hidden">
            <img
              src="/g101/fence-restaurant.jpg"
              alt="A geofence perimeter drawn around a restaurant capturing nearby devices"
              className="w-full h-auto object-cover"
              data-testid="g101-hero-image"
            />
          </div>
        </div>
      </section>

      {/* Military origins */}
      <section className="py-24 bg-[#0d0d0d] border-b border-[#d4af37]/10" data-testid="g101-military">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-4">
            <Satellite size={18} strokeWidth={1.3} className="text-[#d4af37]" />
            <div className="kicker">Born From Military Technology</div>
          </div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] mb-8">
            MILITARY-GRADE <span className="text-[#d4af37]">INTELLIGENCE.</span>
          </h2>
          <p className="text-white/70 text-[15px] leading-relaxed max-w-[720px] mb-12">
            The technology behind geofencing was born in signals and geospatial
            intelligence — the same disciplines used for counterterrorism,
            pattern-of-life tracking and border surveillance. Today, that
            precision powers the future of marketing.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#d4af37]/10">
            <div className="bg-[#0b0b0b] p-10" data-testid="g101-sigint">
              <div className="text-[#d4af37] text-[13px] tracking-[0.3em] mb-3">SIGINT</div>
              <h3 className="text-white text-[18px] tracking-[0.08em] uppercase mb-4">Signals Intelligence</h3>
              <p className="text-white/60 text-[14px] leading-relaxed">Device activity — signals, identifiers and communication. The digital fingerprint every device leaves behind.</p>
            </div>
            <div className="bg-[#0b0b0b] p-10" data-testid="g101-geoint">
              <div className="text-[#d4af37] text-[13px] tracking-[0.3em] mb-3">GEOINT</div>
              <h3 className="text-white text-[18px] tracking-[0.08em] uppercase mb-4">Geospatial Intelligence</h3>
              <p className="text-white/60 text-[14px] leading-relaxed">Location and movement — patterns, zones and behavior over geography. Where devices go, and when.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-24" data-testid="g101-steps">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="kicker mb-4">The Process</div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] mb-14">
            FOUR STEPS TO <span className="text-[#d4af37]">PRECISION.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#d4af37]/10">
            {steps.map((s, i) => (
              <div key={s.title} className="bg-[#0b0b0b] hover:bg-[#111111] p-10 transition-colors duration-500 reveal" data-testid={`g101-step-${i}`}>
                <div className="text-[#d4af37] mb-6">{s.icon}</div>
                <div className="text-[12px] tracking-[0.25em] text-white/40 mb-2">STEP 0{i + 1}</div>
                <h3 className="text-white text-[20px] tracking-[0.08em] uppercase font-medium mb-4">{s.title}</h3>
                <p className="text-white/65 text-[14px] leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Two main uses */}
      <section className="py-24 bg-[#0d0d0d] border-y border-[#d4af37]/10" data-testid="g101-uses">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="kicker mb-4">Two Main Uses</div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] mb-14">
            SEE IT IN <span className="text-[#d4af37]">ACTION.</span>
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div className="reveal" data-testid="g101-use-competitor">
              <div className="border border-[#d4af37]/15 overflow-hidden mb-6">
                <img src="/g101/fence-storefront.jpg" alt="Geofence perimeter around a competitor storefront" className="w-full h-auto object-cover" />
              </div>
              <h3 className="text-white text-[20px] tracking-[0.12em] uppercase mb-3">Competitor Targeting</h3>
              <p className="text-white/65 text-[14px] leading-relaxed max-w-[520px]">
                Fence a competitor's storefront and capture every device that walks
                their floor. Their foot traffic becomes your audience — served your
                offer for weeks after they leave.
              </p>
            </div>
            <div className="reveal" data-testid="g101-use-household">
              <div className="border border-[#d4af37]/15 overflow-hidden mb-6">
                <img src="/g101/fence-households.jpg" alt="Geofence perimeter around residential households" className="w-full h-auto object-cover" />
              </div>
              <h3 className="text-white text-[20px] tracking-[0.12em] uppercase mb-3">Household Targeting</h3>
              <p className="text-white/65 text-[14px] leading-relaxed max-w-[520px]">
                Fence entire neighborhoods and layer household income, life stage and
                lifestyle signals — putting your brand in the living rooms of the
                exact households most likely to buy.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Data sources */}
      <section className="py-24" data-testid="g101-data">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div>
            <div className="kicker mb-4">The Fuel</div>
            <h2 className="h-display text-white text-[36px] sm:text-[52px] mb-10">
              WHERE THE DATA<br /><span className="text-[#d4af37]">COMES FROM.</span>
            </h2>
            <div className="space-y-px bg-[#d4af37]/10">
              {dataSources.map((d) => (
                <div key={d.source} className="bg-[#0b0b0b] p-6 flex gap-5 items-start reveal" data-testid={`g101-source-${d.source.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="text-[#d4af37] mt-0.5">{d.icon}</span>
                  <span>
                    <span className="block text-white text-[14px] tracking-[0.12em] uppercase mb-1">{d.source}</span>
                    <span className="block text-white/60 text-[13px]">{d.def}</span>
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-8 text-[13px] tracking-[0.18em] uppercase text-[#d4af37]" data-testid="g101-intel-formula">
              Device + Location + Behavior = Intent
            </p>
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section className="py-24 bg-[#0d0d0d] border-y border-[#d4af37]/10" data-testid="g101-privacy">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <ShieldCheck size={18} strokeWidth={1.3} className="text-[#d4af37]" />
              <div className="kicker">Data Privacy</div>
            </div>
            <h2 className="h-display text-white text-[36px] sm:text-[48px] mb-6">
              WE TARGET BEHAVIOR —<br /><span className="text-[#d4af37]">NOT INDIVIDUALS.</span>
            </h2>
            <ul className="space-y-4">
              {privacyPoints.map((p, i) => (
                <li key={p} className="flex items-start gap-4 text-white/70 text-[14px] leading-relaxed" data-testid={`g101-privacy-point-${i}`}>
                  <Check size={16} strokeWidth={2} className="text-[#d4af37] mt-0.5 shrink-0" />
                  {p}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Psychology of ad performance */}
      <section className="py-24" data-testid="g101-psychology">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-4">
            <Eye size={18} strokeWidth={1.3} className="text-[#d4af37]" />
            <div className="kicker">The Psychology Of Ad Performance</div>
          </div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] mb-4">
            WHY SOME ADS <span className="text-[#d4af37]">WIN.</span>
          </h2>
          <p className="text-white/60 text-[14px] tracking-wide mb-14 max-w-[560px]">
            Attention → Emotion → Clarity → Action. Small creative shifts create
            exponential impact at scale. Which ad would you click?
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-[#d4af37]/10">
            {psychology.map((p) => (
              <div key={p.id} className="bg-[#0b0b0b] p-10 reveal" data-testid={`g101-psych-${p.id}`}>
                <div className="grid grid-cols-2 gap-4 mb-8">
                  <AdExample img={p.imgA} label="AD 1" winner={p.winner === "A"} />
                  <AdExample img={p.imgB} label="AD 2" winner={p.winner === "B"} />
                </div>
                <div className="flex items-baseline gap-3 mb-3">
                  <span className="h-display text-[#d4af37] text-[34px] leading-none">{p.stat}</span>
                  <span className="text-[11px] tracking-[0.22em] uppercase text-white/50">{p.statLabel}</span>
                </div>
                <h3 className="text-white text-[16px] tracking-[0.15em] uppercase mb-3">{p.title}</h3>
                <p className="text-white/60 text-[13px] leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Video */}
      <section className="py-24 bg-[#0d0d0d] border-b border-[#d4af37]/10" data-testid="g101-video">
        <div className="max-w-[1100px] mx-auto px-6 md:px-10">
          <div className="kicker mb-4 text-center">Watch</div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] mb-12 text-center">
            GEOFENCING IN <span className="text-[#d4af37]">60 SECONDS.</span>
          </h2>
          <div className="relative border border-[#d4af37]/25 overflow-hidden" style={{ paddingBottom: "56.25%" }}>
            <iframe
              className="absolute inset-0 w-full h-full"
              src="https://www.youtube.com/embed/fVR5tnXaxRE?rel=0"
              title="Geofencing explained — Rae Enterprise"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="g101-video-iframe"
            />
          </div>
        </div>
      </section>

      {/* Where your ads appear */}
      <section className="py-24" data-testid="g101-placements">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-4">
            <MonitorPlay size={18} strokeWidth={1.3} className="text-[#d4af37]" />
            <div className="kicker">Where Your Ads Appear</div>
          </div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] mb-4">
            EVERY SCREEN <span className="text-[#d4af37]">THEY OWN.</span>
          </h2>
          <p className="text-white/60 text-[14px] tracking-wide mb-14 max-w-[600px]">
            Once someone crosses your fence, your brand follows them across the
            living-room TV, the phone in their hand and the sites they read —
            600,000+ premium apps, websites and streaming platforms.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-px bg-[#d4af37]/10">
            {placements.map((pl, i) => (
              <div key={pl.title} className="bg-[#0b0b0b] hover:bg-[#111111] p-10 lg:p-12 transition-colors duration-500 reveal flex flex-col" data-testid={`g101-placement-${i}`}>
                <div className="text-[#d4af37] mb-6">{pl.icon}</div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">{pl.kicker}</div>
                <h3 className="text-white text-[20px] tracking-[0.08em] uppercase font-medium mb-4">{pl.title}</h3>
                <p className="text-white/65 text-[14px] leading-relaxed mb-8">{pl.desc}</p>
                <ul className="space-y-3 mb-8">
                  {pl.points.map((pt) => (
                    <li key={pt} className="flex items-start gap-3 text-[13px] text-white/60">
                      <span className="mt-2 w-1 h-1 bg-[#d4af37] inline-block shrink-0" />
                      {pt}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto pt-6 border-t border-white/5">
                  <span className="text-[10px] tracking-[0.22em] uppercase text-white/40">Best For</span>
                  <p className="text-[#d4af37] text-[13px] mt-1">{pl.bestFor}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key terms */}
      <section className="py-24 bg-[#0d0d0d] border-y border-[#d4af37]/10" data-testid="g101-terms">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="kicker mb-4">Speak The Language</div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] mb-14">
            KEY <span className="text-[#d4af37]">TERMS.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#d4af37]/10">
            {terms.map((t) => (
              <div key={t.term} className="bg-[#0b0b0b] p-8 flex gap-5 reveal" data-testid={`g101-term-${t.term.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="text-[#d4af37] mt-1">{t.icon}</span>
                <span>
                  <span className="block text-white text-[14px] tracking-[0.15em] uppercase mb-2">{t.term}</span>
                  <span className="block text-white/60 text-[13px] leading-relaxed">{t.def}</span>
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-24" data-testid="g101-usecases">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10">
          <div className="kicker mb-4">Where It Wins</div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] mb-14">
            COMMON <span className="text-[#d4af37]">USE CASES.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#d4af37]/10">
            {useCases.map((u, i) => (
              <div key={u.title} className="bg-[#0b0b0b] hover:bg-[#111111] p-10 transition-colors duration-500 reveal" data-testid={`g101-usecase-${i}`}>
                <div className="text-[#d4af37] mb-5">{u.icon}</div>
                <h3 className="text-white text-[15px] tracking-[0.15em] uppercase mb-3">{u.title}</h3>
                <p className="text-white/60 text-[13px] leading-relaxed">{u.desc}</p>
              </div>
            ))}
          </div>
          <div className="mt-10">
            <Link to="/case-studies" className="kicker text-[#d4af37] hover:text-[#f2c66d]" data-testid="g101-casestudies-link">
              See real campaign results →
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 bg-[#111111] border-y border-[#d4af37]/10" data-testid="g101-faq">
        <div className="max-w-[900px] mx-auto px-6 md:px-10">
          <div className="kicker mb-4 text-center">Questions</div>
          <h2 className="h-display text-white text-[36px] sm:text-[52px] mb-14 text-center">
            GEOFENCING <span className="text-[#d4af37]">FAQ.</span>
          </h2>
          <div className="space-y-px bg-[#d4af37]/10">
            {faqs.map((f, i) => (
              <FaqItem key={f.q} q={f.q} a={f.a} index={i} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" data-testid="g101-cta">
        <div className="max-w-[900px] mx-auto px-6 md:px-10 text-center">
          <div className="kicker mb-6">Ready To Try It?</div>
          <h3 className="h-display text-white text-[32px] sm:text-[48px]">
            BUILD YOUR CAMPAIGN IN<br /><span className="text-[#d4af37]">FIVE MINUTES.</span>
          </h3>
          <Link to="/campaign-builder" className="btn-gold btn-label mt-10 inline-flex" data-testid="g101-builder-cta">
            Open The Campaign Builder <ArrowRight size={16} strokeWidth={1.6} />
          </Link>
        </div>
      </section>
    </SiteShell>
  );
}

function AdExample({ img, label, winner }) {
  return (
    <div className={`relative border ${winner ? "border-[#d4af37]" : "border-white/10"} overflow-hidden`}>
      <img src={img} alt={`${label} creative example`} className="w-full h-full object-cover" />
      <span className={`absolute top-2 left-2 text-[10px] tracking-[0.2em] px-2 py-1 ${winner ? "bg-[#d4af37] text-black" : "bg-black/70 text-white/70"}`}>
        {label}{winner ? " · WINNER" : ""}
      </span>
    </div>
  );
}

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="bg-[#0b0b0b]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-6 text-left p-7 hover:bg-[#101010] transition-colors"
        data-testid={`g101-faq-toggle-${index}`}
      >
        <span className="text-white text-[15px] tracking-wide">{q}</span>
        <ChevronDown size={18} strokeWidth={1.5} className={`text-[#d4af37] shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <p className="px-7 pb-7 text-white/65 text-[14px] leading-relaxed" data-testid={`g101-faq-answer-${index}`}>{a}</p>
      )}
    </div>
  );
}
